/**
 * Oath Resource Service
 * API calls for the Resource Center (videos, guides, categories)
 * Routes to oath/resources/* endpoints in the backend
 *
 * All responses follow the envelope: { "success": true, "data": ... }
 * Errors follow: { "error": { "status": 400, "message": "..." } }
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Extract a human-readable error message from the API error envelope.
 * { "error": { "status": 400, "message": "..." } }
 */
const extractErrorMessage = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || fallback;
};

// ============================================
// RESOURCES

/**
 * Get a paginated, filtered list of resources.
 * body is NOT included — use getResourceById for full guide content.
 *
 * @param {Object} params
 * @param {string} [params.type] - 'video' | 'guide'
 * @param {number} [params.categoryId]
 * @param {string} [params.pageContext] - route slug e.g. 'power-reports'
 * @param {string} [params.search] - max 100 chars
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @returns {{ success: boolean, data: Array, totalCount: number, page: number, pageSize: number, error?: string }}
 */
export const getResources = async ({ type, categoryId, pageContext, search, page = 1, pageSize = 20 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (categoryId) params.set('categoryId', categoryId);
    if (pageContext) params.set('pageContext', pageContext);
    if (search) params.set('search', search);
    params.set('page', page);
    params.set('pageSize', pageSize);

    const response = await authFetch(`${BASE_URL}oath/resources?${params.toString()}`);

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'Failed to load resources');
      return { success: false, data: [], totalCount: 0, error: message };
    }

    const body = await response.json();
    return {
      success: true,
      data: body.data ?? [],
      totalCount: body.totalCount ?? body.data?.length ?? 0,
      page: body.page ?? page,
      pageSize: body.pageSize ?? pageSize,
    };
  } catch (err) {
    console.error('getResources error:', err);
    return { success: false, data: [], totalCount: 0, error: 'Failed to load resources' };
  }
};

/**
 * Get a single resource by ID including full body for guides.
 *
 * @param {number} id
 * @returns {{ success: boolean, resource?: Object, error?: string }}
 */
export const getResourceById = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/resources/${id}`);

    if (response.status === 404) {
      return { success: false, error: 'Resource not found.' };
    }

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'Failed to load resource');
      return { success: false, error: message };
    }

    const body = await response.json();
    return { success: true, resource: body.data };
  } catch (err) {
    console.error('getResourceById error:', err);
    return { success: false, error: 'Failed to load resource' };
  }
};

// ============================================
// CATEGORIES

/**
 * Get all active resource categories ordered by sortOrder.
 *
 * @returns {{ success: boolean, categories: Array, error?: string }}
 */
export const getResourceCategories = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/resources/categories`);

    if (!response.ok) {
      const message = await extractErrorMessage(response, 'Failed to load categories');
      return { success: false, categories: [], error: message };
    }

    const body = await response.json();
    return { success: true, categories: body.data ?? [] };
  } catch (err) {
    console.error('getResourceCategories error:', err);
    return { success: false, categories: [], error: 'Failed to load categories' };
  }
};
